import {
    boundAttributeSuffix,
    HTML_RESULT,
    marker,
    parseTemplateLiteral,
    rawTextElement,
    walker,
} from './html-parser';
import { insertElements, nameConverter } from './render-api';
import type {
    ElementConfig,
    ElementChild,
    AllowedTagName,
} from '../components/ui-element.type';

const resolveAttributeValue = (
    value: string,
    renderNodes: Array<unknown>
): unknown => {
    const attributeParts = value.split(marker);
    // Single value mode
    if (
        attributeParts.length === 2 &&
        attributeParts[0] === '' &&
        attributeParts[1] === ''
    ) {
        return renderNodes.shift();
    }
    if (attributeParts.length > 0) {
        return attributeParts.reduce(
            (acc, entry, index, list) =>
                `${acc}${entry}${index + 1 < list.length ? String(renderNodes.shift()) : ''}`,
            ''
        );
    }
    return null;
};

const populateElementNode = (
    node: Element,
    attributes: Array<string>,
    renderNodes: Array<unknown>,
    replacementMap: WeakMap<Node, unknown>
): void => {
    const config: ElementConfig = {
        tagName: node.localName as AllowedTagName,
        attributes: {},
        events: {},
        props: {},
        children: [] as Array<ElementChild>,
    };
    if (node.hasAttributes()) {
        for (const name of node.getAttributeNames()) {
            if (
                name.endsWith(boundAttributeSuffix) ||
                name.startsWith(marker)
            ) {
                const realName = attributes.shift();
                if (realName === undefined) {
                    continue;
                }

                const rawValue = node.getAttribute(
                    realName.toLowerCase() + boundAttributeSuffix
                );
                const value = rawValue ?? '';
                const m = /([.?@])?(.*)/.exec(realName);
                if (!m) {
                    continue;
                }
                const attributeValue = resolveAttributeValue(
                    value,
                    renderNodes
                );
                switch (m[1]) {
                    case '@':
                        if (typeof attributeValue === 'function') {
                            config.events![m[2]] = attributeValue as (
                                event?: Event | CustomEvent<unknown>
                            ) => void;
                        }
                        break;
                    case '.':
                        config.props![m[2]] = attributeValue;
                        break;
                    default:
                        if (
                            attributeValue &&
                            typeof attributeValue === 'object'
                        ) {
                            (config.attributes as Record<string, unknown>)[
                                nameConverter.attrToProperty(m[2])
                            ] = attributeValue;
                        } else {
                            (config.attributes as Record<string, unknown>)[
                                m[2]
                            ] = attributeValue;
                        }
                        break;
                }
            } else {
                (config.attributes as Record<string, unknown>)[name] =
                    node.getAttribute(name);
            }
        }
    }

    if (rawTextElement.test(config.tagName ?? '')) {
        const content = node.innerHTML;
        node.innerHTML = '';
        const result = content.replaceAll(marker, () =>
            String(renderNodes.shift())
        );
        (config.children as Array<ElementChild>).push(result);
    }
    replacementMap.set(node, config);
};

const populateCommentNode = (
    node: Node,
    _attributes: Array<string>,
    renderNodes: Array<unknown>,
    replacementMap: WeakMap<Node, unknown>
): void => {
    replacementMap.set(node, renderNodes.shift());
};

const populateChild = (
    node: Node,
    attributes: Array<string>,
    renderNodes: Array<unknown>,
    replacementMap: WeakMap<Node, unknown>
): void => {
    switch (node.nodeType) {
        case Node.ELEMENT_NODE:
            populateElementNode(
                node as Element,
                attributes,
                renderNodes,
                replacementMap
            );
            break;
        case Node.COMMENT_NODE:
            populateCommentNode(node, attributes, renderNodes, replacementMap);
            break;
        default:
            break;
    }
};

const populateNodeChildren = (
    rootNode: Node,
    renderNodes: WeakMap<Node, unknown>,
    trusted: boolean
): Array<unknown> => {
    return Array.from(rootNode.childNodes, (child: Node) => {
        const hasReplacement = renderNodes.has(child);
        let replacement = hasReplacement ? renderNodes.get(child) : null;
        if (typeof replacement === 'number') {
            replacement = String(replacement);
        }
        switch (child.nodeType) {
            case Node.ELEMENT_NODE:
                if (hasReplacement) {
                    if (!rawTextElement.test((child as Element).tagName)) {
                        (replacement as ElementConfig).children =
                            populateNodeChildren(
                                child,
                                renderNodes,
                                trusted
                            ) as Array<ElementChild>;
                    }
                    return replacement;
                }
                return child;
            case Node.COMMENT_NODE:
                return hasReplacement ? replacement : child;
            case Node.TEXT_NODE:
                return trusted ? (child as Text).textContent : child;
            default:
                return child;
        }
    });
};

const populateTemplate = (
    template: HTMLTemplateElement,
    attrNames: Array<string | undefined>,
    values: Array<unknown>,
    trusted: boolean
): Array<unknown> => {
    let node: Node | null;
    walker.currentNode = template.content;
    const attributeMap: Array<string> = attrNames.map((name) => name ?? '');
    const renderNodes = values;
    const replacementMap = new WeakMap<Node, unknown>();
    while ((node = walker.nextNode()) !== null) {
        populateChild(node, attributeMap, renderNodes, replacementMap);
    }
    return populateNodeChildren(template.content, replacementMap, trusted);
};

const processHTML = <T>(
    trusted: boolean,
    strings: TemplateStringsArray | Array<string>,
    ...renderNodes: Array<unknown>
): DocumentFragment => {
    const { tpl: rawTemplate, attrNames } = parseTemplateLiteral(
        strings,
        HTML_RESULT
    );
    const children = populateTemplate(
        rawTemplate,
        attrNames,
        renderNodes,
        trusted
    );
    const resultTemplate = document.createElement('template');
    insertElements(
        resultTemplate.content,
        children as Array<ElementConfig | HTMLElement | string>,
        undefined,
        trusted
    );

    return resultTemplate.content;
};

/**
 * Creates safe HTML template from string literal.
 * @memberof UI
 * @alias html
 * @param {TemplateStringsArray | Array<string>} strings
 * @param {...unknown} renderNodes
 * @returns {DocumentFragment}
 */
export const html = (
    strings: TemplateStringsArray | Array<string>,
    ...renderNodes: Array<unknown>
): DocumentFragment => {
    return processHTML(false, strings, ...renderNodes);
};

/**
 * Creates unsafe HTML template from string literal.
 * @memberof UI
 * @alias trustedHTML
 * @param {TemplateStringsArray | Array<string>} strings
 * @param {...unknown} renderNodes
 * @returns {DocumentFragment}
 */
export const trustedHTML = (
    strings: TemplateStringsArray | Array<string>,
    ...renderNodes: Array<unknown>
): DocumentFragment => {
    return processHTML(true, strings, ...renderNodes);
};

/**
 * Creates safe HTML element from string literal.
 * @memberof UI
 * @alias element
 * @param {TemplateStringsArray | Array<string>} strings
 * @param {...unknown} renderNodes
 * @returns {HTMLElement | null}
 */
export const element = <T extends HTMLElement = HTMLElement>(
    strings: TemplateStringsArray | Array<string>,
    ...renderNodes: Array<unknown>
): T => {
    return processHTML(false, strings, ...renderNodes).firstElementChild as T;
};

/**
 * Creates unsafe html element from string literal.
 * @memberof UI
 * @alias trustedElement
 * @param {TemplateStringsArray | Array<string>} strings
 * @param {...unknown} renderNodes
 * @returns {Element | null}
 */
export const trustedElement = (
    strings: TemplateStringsArray | Array<string>,
    ...renderNodes: Array<unknown>
): Element | null => {
    return processHTML(true, strings, ...renderNodes).firstElementChild;
};

/**
 * Creates CSS template from string literal.
 * @memberof UI
 * @alias css
 * @param {TemplateStringsArray | Array<string>} strings
 * @param {...(string | function)} keys
 * @returns {string}
 */
export const css = (
    strings: TemplateStringsArray | Array<string>,
    ...keys: Array<string | (() => string)>
): string => {
    return strings.reduce((tpl, item, index) => {
        let entity = keys[index - 1];
        if (typeof entity === 'function') {
            entity = entity();
        }
        return tpl + (entity ?? '') + item;
    }, '');
};
